import { Button } from '@brightdrop/bd-ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { SingleAlertRecord } from '~/api/generated-api';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { useOrgId } from '~/common/hooks/useOrgId';
import { AlertEvent } from '~/common/models/alert.model';
import {
  HEAP_ENTRY_POINTS,
  type HeapNavigationState,
} from '~/common/models/heap.model';

type NotificationViewButtonProps = {
  item: SingleAlertRecord;
  /** Fired when the view button is clicked, before navigation. */
  onClick?: () => void;
};

const NotificationViewButton = ({
  item,
  onClick,
}: NotificationViewButtonProps): JSX.Element | null => {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['settings']);
  const navigate = useNavigate();
  const organizationsId = useOrgId();

  const eventType = item?.event?.event ?? item?.lastGroupEvent?.event?.event;
  const contentVars =
    item?.lastGroupEvent?.event?.contentVariableMap ??
    item?.event?.contentVariableMap ??
    {};
  const vin = (contentVars as Record<string, string>)?.vin || '';
  const eventId =
    item?.lastGroupEvent?.id ??
    (item?.event as { id?: string })?.id ??
    item?.id ??
    '';
  const tripId = (contentVars as Record<string, string>)?.trip_id ?? '';
  const vehicleId =
    (contentVars as Record<string, string>)?.alert_objectid ?? '';
  const startTimeUtcRaw =
    (contentVars as Record<string, string>)?.start_time_utc ?? '';
  const startTimeParsed = startTimeUtcRaw ? new Date(startTimeUtcRaw) : null;
  const startTime =
    startTimeParsed && !Number.isNaN(startTimeParsed.getTime())
      ? encodeURIComponent(startTimeParsed.toISOString())
      : '';

  if (eventType === AlertEvent.GEOFENCE_ENTRY_EXIT && vin && organizationsId) {
    return (
      <Button
        variant="secondary"
        size="medium"
        onClick={() => {
          onClick?.();
          navigate(`/organizations/${organizationsId}/vehicles/map/${vin}`);
        }}
        data-testid="view-vehicle-button"
      >
        {translateText('settings:notifications.messages.viewVehicle')}
      </Button>
    );
  }

  if (
    eventType === AlertEvent.SAFETY_EVENT &&
    organizationsId &&
    eventId &&
    tripId
  ) {
    return (
      <Button
        variant="secondary"
        size="medium"
        onClick={() => {
          onClick?.();
          const navigationState: HeapNavigationState = {
            entryPoint: HEAP_ENTRY_POINTS.NOTIFICATION_FEED,
          };
          navigate(
            `/organizations/${organizationsId}/safety/events/${eventId}/trip/${tripId}`,
            { state: navigationState }
          );
        }}
        data-testid="view-event-button"
      >
        {translateText('settings:notifications.messages.viewEvent')}
      </Button>
    );
  }

  if (eventType === AlertEvent.LOW_TRIP_SCORE && organizationsId && vehicleId) {
    const navigationPath = tripId
      ? `/organizations/${organizationsId}/vehicles/${vehicleId}/trips/${tripId}`
      : `/organizations/${organizationsId}/vehicles/${vehicleId}/trips`;
    const navigationPathWithTripDate = startTime
      ? `${navigationPath}?tripDate=${startTime}`
      : navigationPath;
    return (
      <Button
        variant="secondary"
        size="medium"
        onClick={() => {
          onClick?.();
          navigate(navigationPathWithTripDate);
        }}
        data-testid="view-trip-button"
      >
        {translateText('settings:notifications.messages.viewTrip')}
      </Button>
    );
  }

  return null;
};

export default NotificationViewButton;
